import { TemplateResult, html, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-card.css.lit';
import '../tufts-heading/tufts-heading';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import '../tufts-button/tufts-button';
import { ResizeController } from '../../controllers/resize-controller';

/**
 * The Tufts Card general component
 * Simple layout and color aware component for extending
 * @element tufts-card
 *
 * @slot title - if title exists for special layout
 * @slot primary-content - most often used for media content
 * @slot secondary-content - text content and buttons
 *
 * @attr primary-content - Side that primary content is placed(left or right)
 * @attr background - color of background
 * @attr gap - space between primary and secondary content
 * @attr vertical-trigger-class - class name when vertical
 * @attr vertical-trigger-width - number at which change to vertical
 * @attr card-with-title - special layout for card that has title
 * @attr layout - 50-50 or two-thirds grid layout
 */
@customElement('tufts-card')
export class tuftsCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);
  resizeController: ResizeController;

  // The resizeController is initialized here because verticalTriggerWidth is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [this.verticalTriggerWidth],
    });
  }

  /**
   * Side of content
   */
  @property({ type: String, reflect: true, attribute: 'primary-content' })
  primaryContent: string;

  /**
   * Background color
   */
  @property({ type: String, reflect: true, attribute: 'background' })
  background = 'white';

  /**
   * Padding for content
   */
  @property({ type: String, reflect: true, attribute: 'padding' })
  padding: string;

  /**
   * Gap between primary & secondary content
   */
  @property({ type: String, reflect: true, attribute: 'gap' })
  gap: string;

  /**
   * Class name for when changing from horizontal to vertical layout
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'vertical-trigger-class',
  })
  verticalTriggerClass: string;

  /**
   * Width at which layout changes from horizontal to vertical
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'vertical-trigger-width',
  })
  verticalTriggerWidth = 768;

  /**
   * Check for if card has a title
   */
  @property({ type: Boolean, reflect: true, attribute: 'card-with-title' })
  cardWithTitle = false;

  /**
   * Type of layout: Expected 50-50 or two-thirds
   */
  @property({ type: String, reflect: true, attribute: 'layout' })
  layout: string;

  /**
   * Parent theme (dark/light) for hover effects
   */
  @property({ type: String, reflect: true, attribute: 'parent-theme' })
  parentTheme: string;

  isMobile: boolean;

  exist(slotName: string) {
    const slottedNodes = Array.from(
      this.querySelectorAll(`[slot=${slotName}]`)
    );

    if (slottedNodes.length) {
      return slottedNodes;
    } else {
      return null;
    }
  }

  /**
   * Sets the grid layout based on passed layout property
   */
  layoutGridSetup() {
    switch (this.layout) {
      case 'two-third':
        if (this.primaryContent === 'second') {
          return '1fr 2fr';
        } else {
          return '2fr 1fr';
        }
      case '50-50':
      default:
        return '1fr 1fr';
    }
  }

  /**
   * Checks side of content
   */
  isOrderReversed() {
    if (this.isMobile || this.primaryContent !== 'second') {
      return false;
    }
    return true;
  }

  /**
   * Template partial for default order and mobile devices
   * @returns TemplateResult
   */
  regularOrderTemplate(_themeClass: string): TemplateResult {
    return html`
      ${this.ngSlot.conditionalNGSlot('title', false)}
      ${this.ngSlot.conditionalNGSlot('primary-content', false)}
      ${this.ngSlot.conditionalNGSlot('secondary-content', false)}
    `;
  }

  /**
   * Template partial for reverse order on desktop devices
   * @returns TemplateResult
   */

  reverseOrderTemplate(_themeClass: string): TemplateResult {
    return html`
      ${this.ngSlot.conditionalNGSlot('title', false)}
      ${this.ngSlot.conditionalNGSlot('secondary-content', false)}
      ${this.ngSlot.conditionalNGSlot('primary-content', false)}
    `;
  }

  /**
   * Background aware button template
   * Filled button
   */
  ctaTemplate1(slotName: string, theme: string): TemplateResult | null {
    if (this.ngSlot.exist(slotName)) {
      return html`
        <tufts-button
          class="${slotName}"
          button-variant="${theme === 'theme-light' ? 'primary' : 'reversed'}"
        >
          ${this.ngSlot.renderInShadow(slotName)}
        </tufts-button>
      `;
    }
    return null;
  }

  /**
   * Background aware button template
   * Outlined button
   */
  ctaTemplate2(slotName: string, theme: string): TemplateResult | null {
    if (this.ngSlot.exist(slotName)) {
      return html`
        <tufts-button
          class="${slotName}"
          button-variant="${theme === 'theme-light'
            ? 'primary outlined'
            : 'reversed outlined'}"
        >
          ${this.ngSlot.renderInShadow(slotName)}
        </tufts-button>
      `;
    } else {
      return null;
    }
  }

  /**
   * Checks background and applies value to themeClass variable
   */
  setColorTemplate() {
    // set light/dark theme
    let themeClass = '';
    switch (this.background) {
      case 'blue_dark':
      case 'blue':
      case 'green':
      case 'purple_dark':
      case 'purple':
        themeClass = 'theme-dark';
        break;
      default:
      case 'tint':
      case 'grey':
      case 'white':
        themeClass = 'theme-light';
        break;
    }
    return themeClass;
  }

  /**
   * Checks if container that component is placed on has a background color
   */
  checkParentNodeContainer() {
    // Only skip if background is explicitly transparent and theme is light
    if (
      this.background === 'transparent' &&
      this.setColorTemplate() === 'theme-light'
    ) {
      return;
    }

    // get the parent node component
    const parentElement = this.closest('tufts-container') as Element;
    let parentBackgroundColorAttribute: string | null = '';
    // if a parent element exists, check what the value of background-color is
    if (parentElement) {
      parentBackgroundColorAttribute =
        parentElement.getAttribute('background-color');
    }

    const ParentThemeDarkOption = [
      'blue',
      'blue_dark',
      'purple',
      'purple_dark',
      'green',
    ];

    // Store parent theme for hover effects
    if (
      parentBackgroundColorAttribute &&
      ParentThemeDarkOption.includes(parentBackgroundColorAttribute)
    ) {
      this.parentTheme = 'theme-dark';
      // Only update background if it was transparent
      if (this.background === 'transparent') {
        this.background = parentBackgroundColorAttribute;
      }
    } else {
      this.parentTheme = 'theme-light';
    }
  }

  /**
   * Main render
   */
  render(): TemplateResult {
    const themeClass = this.setColorTemplate();

    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    const cardWrapperClasses = {
      'card-wrapper': true,
      'card-wrapper--with-title': this.cardWithTitle,
      'mobile': this.isMobile,
      'primary-content': this.primaryContent,
    };
    return html`
      <style>
        .card {
          --card-gap: ${this.gap
            ? 'var(--' + this.gap + ')'
            : 'var(--space-m-l)'};
          --card-padding: ${this.padding
            ? 'var(--' + this.padding + ')'
            : 'var(--space-s-4xl)'};

          --card-template-columns: ${this.layoutGridSetup()};
        }
      </style>
      <article
        class="card ${themeClass} ${this.isOrderReversed()
          ? 'reverse-order'
          : 'regular-order'}"
      >
        <div class=${classMap(cardWrapperClasses)}>
          ${this.isOrderReversed()
            ? this.reverseOrderTemplate(themeClass)
            : this.regularOrderTemplate(themeClass)}
        </div>
      </article>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-card': tuftsCard;
  }
}
